import React from 'react';

/**
 * Component to safely render SVG code from strings
 * Handles both inline SVG strings and fallback icons
 */
const SVGRenderer = ({
  svgCode,
  className = '',
  width = 24,
  height = 24,
  fallbackType = 'sublesson',
  style = {}
}) => {
  // If no SVG code provided, use fallback
  if (!svgCode) {
    svgCode = getFallbackSVG(fallbackType);
  }

  // Clean and validate SVG code
  const cleanSVG = cleanSVGCode(svgCode);

  return (
    <div
      className={`svg-renderer ${className}`}
      style={{
        width,
        height,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...style
      }}
      dangerouslySetInnerHTML={{ __html: cleanSVG }}
    />
  );
};

/**
 * Cleans SVG code for safe rendering
 */
function cleanSVGCode(svgCode) {
  if (!svgCode) return '';

  // Remove any markdown formatting
  let cleaned = svgCode.replace(/```svg\n?/g, '').replace(/```\n?/g, '');

  // Ensure it starts with <svg
  if (!cleaned.trim().startsWith('<svg')) {
    const svgMatch = cleaned.match(/<svg[\s\S]*<\/svg>/);
    if (svgMatch) {
      cleaned = svgMatch[0];
    }
  }

  // Ensure proper viewBox and dimensions
  if (!cleaned.includes('viewBox')) {
    cleaned = cleaned.replace('<svg', '<svg viewBox="0 0 64 64"');
  }

  // Add width and height if not present
  if (!cleaned.includes('width=')) {
    cleaned = cleaned.replace('<svg', '<svg width="100%" height="100%"');
  }

  return cleaned.trim();
}

/**
 * Fallback SVG icons when AI generation fails
 */
function getFallbackSVG(type) {
  const icons = {
    course: `<svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
      <rect x="8" y="12" width="48" height="40" rx="4" fill="#4FACFE" opacity="0.2"/>
      <rect x="12" y="16" width="40" height="32" rx="2" fill="#4FACFE"/>
      <rect x="16" y="20" width="32" height="2" fill="white"/>
      <rect x="16" y="26" width="24" height="2" fill="white" opacity="0.8"/>
      <rect x="16" y="32" width="28" height="2" fill="white" opacity="0.8"/>
      <circle cx="48" cy="20" r="6" fill="#00F2FE"/>
    </svg>`,
    lesson: `<svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
      <rect x="12" y="8" width="40" height="48" rx="4" fill="#10B981" opacity="0.2"/>
      <rect x="16" y="12" width="32" height="40" rx="2" fill="#10B981"/>
      <rect x="20" y="16" width="24" height="2" fill="white"/>
      <rect x="20" y="22" width="20" height="2" fill="white" opacity="0.8"/>
      <rect x="20" y="28" width="22" height="2" fill="white" opacity="0.8"/>
      <circle cx="44" cy="16" r="4" fill="#00F2FE"/>
    </svg>`,
    sublesson: `<svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
      <rect x="16" y="16" width="32" height="32" rx="4" fill="#8B5CF6" opacity="0.2"/>
      <rect x="20" y="20" width="24" height="24" rx="2" fill="#8B5CF6"/>
      <rect x="24" y="24" width="16" height="2" fill="white"/>
      <rect x="24" y="30" width="12" height="2" fill="white" opacity="0.8"/>
      <rect x="24" y="36" width="14" height="2" fill="white" opacity="0.8"/>
    </svg>`
  };

  return icons[type] || icons.sublesson;
}

export default SVGRenderer;
